import type {Size} from '@clappr/core'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import {ImaPlayback, type ImaEvent} from '../ima-playback'
import * as skins from './skins'

export class OverlayPlayback extends ImaPlayback {
  protected static bannerDisplayContainer: any = null
  protected static loaded = false

  public static async preload(options: Options): Promise<void> {
    if (OverlayPlayback.loaded) return
    OverlayPlayback.loaded = true

    const skinName = options.mediaControl?.skinName
    const loadSkin = (skinName && (skins as any)[skinName]) || skins.rambler

    await Promise.all([super.preload(options), loadSkin()])
  }

  public static destroy(): void {
    OverlayPlayback.bannerDisplayContainer?.destroy()
    delete OverlayPlayback.bannerDisplayContainer
  }

  protected get adDisplayContainer(): any {
    return OverlayPlayback.bannerDisplayContainer
  }

  protected set adDisplayContainer(adDisplayContainer: any) {
    OverlayPlayback.bannerDisplayContainer = adDisplayContainer
  }

  public get name(): string {
    return 'overlayPlayback'
  }

  protected createAdDisplayContainer(): void {
    if (!this.adDisplayContainer) {
      this.adDisplayContainer = new this.ima.AdDisplayContainer(
        this.options.containerElement
      )
      this.adDisplayContainer.initialize()
    }
  }

  protected createAdsRequest(): void {
    const size = this.getSize(this.sources.url)

    this.adsRequest = new this.ima.AdsRequest()
    this.adsRequest.adTagUrl = this.sources.url
    this.adsRequest.vastLoadTimeout = OverlayPlayback.VAST_LOAD_TIMEOUT
    this.adsRequest.nonLinearAdSlotWidth = size.width
    this.adsRequest.nonLinearAdSlotHeight = size.height
    this.options.containerElement.style.height = `${size.height}px`
    // assume playback is consented by user
    this.adsRequest.setAdWillAutoPlay(true)
    this.adsRequest.setAdWillPlayMuted(this.options.muted)
  }

  protected async loadAdsManager(): Promise<void> {
    let adsManagerLoadedEvent: any

    try {
      this.adsLoader = new this.ima.AdsLoader(this.adDisplayContainer)
      adsManagerLoadedEvent = await new Promise((resolve, reject) => {
        this.adsLoader.addEventListener(
          this.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
          resolve
        )
        this.adsLoader.addEventListener(
          this.ima.AdErrorEvent.Type.AD_ERROR,
          reject
        )
        this.adsLoader.requestAds(this.adsRequest)
      })
    } catch (error: any) {
      this.onAdError(error)

      return
    }

    const adsRenderingSettings = new this.ima.AdsRenderingSettings()

    adsRenderingSettings.useStyledNonLinearAds = true
    // plugin will handle playback state when ad has completed
    adsRenderingSettings.loadVideoTimeout = OverlayPlayback.LOAD_VIDEO_TIMEOUT
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = false
    this.adsManager = adsManagerLoadedEvent.getAdsManager(
      this.options.videoElement,
      adsRenderingSettings
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.LOADED,
      (event: ImaEvent) => this.onAdLoaded(event)
    )
    this.adsManager.addEventListener(
      this.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdError
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.USER_CLOSE,
      this.onAdUserClose
    )
  }

  protected initAdsManager(): void {
    if (this.adsManager) {
      const size = this.getSize(this.sources.url)

      this.adsManager.init(size.width, size.height, this.ima.ViewMode.NORMAL)
      this.trigger(Events.ADPLAYBACK_READY, this.sources)
    }
  }

  private getSize(url: string): Size {
    const {offsetWidth} = this.options.videoElement
    const sizeMatch = url.match(/sz=(\d+)x(\d+)/)
    let width: number
    let height: number

    if (sizeMatch) {
      width = parseInt(sizeMatch[1], 10)
      height = parseInt(sizeMatch[2], 10)

      if (width > offsetWidth) {
        height = (height * offsetWidth) / width
        width = offsetWidth
      }
    } else {
      width = offsetWidth
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      height = width < 400 ? 50 : 100

      if (url.match('advarkads.com')) {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        height = 200
      }

      if (url.match('ads.adfox.ru') || url.match('ssp.rambler.ru')) {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        height = width < 400 ? 55 : 105
      }
    }

    return {width, height}
  }

  public resize(): void {
    const size = this.getSize(this.sources.url)

    this.options.containerElement.style.height = `${size.height}px`
    this.adsManager?.resize(size.width, size.height, this.ima.ViewMode.NORMAL)
  }

  public destroy(): void {
    this.adsLoader?.contentComplete()
    this.adsLoader?.destroy()
    this.adsManager?.destroy()
  }
}
